import React, { useContext, useEffect, useState, useRef } from "react";
import TertiaryButton from "../buttons/TertiaryButton";
import Image from "../images/Image";
import { useNavigate } from "react-router";
import { signOut } from "@aws-amplify/auth";
import GlobalContext from "../Context/GlobalContext";
import CustomModal from "../CustomModal";
import { showToast } from "../toastMessage/toastSlice";
import { useDispatch, useSelector } from "react-redux";
import LanguageChanger from "../LanguageChangerDropdown";
import { SideBarContent } from "./SideBarContent";
import useTranslation from "../CustomHooks/useTranslation";
import { fetchAuthSession } from 'aws-amplify/auth';
import NotificationDropdown from "../NotificationDropdown/NotificationDropdown";
import useApi from "../../services/useApi";
import endpoints from "../../services/endpoints";
import { RootState } from "../../store";
import ExchangeRatePill from "../ExchangeRate/ExchangeRatePill";

interface TopbarProps {
  onMenuClick?: () => void;
}

const Topbar: React.FC<TopbarProps> = ({ onMenuClick }) => {
  const buttons = ['Career', 'Blog', 'Job Application'];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setIsLoggedIn } = useContext(GlobalContext)
  const [isOpen, setIsOpen] = useState(false)
  const [isNotificationOpen, setIsNotificationOpen] = useState(false)
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false)
  const notificationRef = useRef<HTMLDivElement>(null);
  const { translate } = useTranslation(SideBarContent);
  const [initials, setInitials] = useState('')
  const { getAPI } = useApi()
  const userInfo = useSelector((state: RootState) => state.userEmail.userInfo);

  const clickLogout = () => {
    setIsLoggedIn(false)
    signOut();
    localStorage.clear();
    dispatch(showToast({ type: "success", message: "Logout successful" }));
    navigate('/');
    setIsOpen(false)
  }

  const getUserName = async () => {
    // First, try to get initials from Redux store (after page loads)
    const firstName = typeof userInfo?.first_name === 'string' ? userInfo.first_name : '';
    const lastName = typeof userInfo?.last_name === 'string' ? userInfo.last_name : '';
    
    // If first_name exists, generate initials from first_name and last_name
    if (firstName) {
      const first = firstName.trim();
      const last = (lastName || '').trim();
      
      // If both first_name and last_name exist: first letter of first_name + first letter of last_name
      if (first && last) {
        setInitials((first[0] + last[0]).toUpperCase());
        return;
      } 
      // If only first_name exists: first letter of first_name
      else if (first) {
        setInitials(first[0].toUpperCase());
        return;
      }
    }

    // Fallback: try to get from auth session token (same way)
    const user = await fetchAuthSession({ forceRefresh: true });
    if (user) {
      const key = user?.tokens?.idToken?.payload?.name;
      if (!key || typeof key !== "string") return "";

      const words = key.trim().split(/\s+/); // Split by spaces

      // If multiple words: first letter of first word + first letter of second word
      if (words.length > 1) {
        setInitials((words[0][0] + words[1][0]).toUpperCase());
      } 
      // If single word: first letter only
      else if (words[0].length > 0) {
        setInitials(words[0][0].toUpperCase());
      }
    }
  }

  const checkUnreadNotifications = async () => {
    try {
      const response = await getAPI(`${endpoints.notifications}?unread_notification=true`);
      if (!response.error && response.data) {
        setHasUnreadNotifications(response.data.unread_notification || false);
      }
    } catch (error) {
      console.error("Error checking unread notifications:", error);
    }
  };

  useEffect(() => {
    getUserName();
    checkUnreadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);
  return (
    <div className="bg-white flex justify-between md:justify-end items-center h-[70px] md:h-[100px]">
      <button
        className="md:hidden pl-4 text-primary_text"
        onClick={onMenuClick}
        aria-label="Open menu"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      {/* Exchange rate pill sits at the left of the header; mr-auto pushes the
          nav to the right without changing the container's justification, so
          the original layout is untouched on breakpoints where the pill is
          hidden. Shown from lg up, where there is room alongside the nav. */}
      <div className="hidden lg:block mr-auto">
        <ExchangeRatePill />
      </div>
      {/* The gap tightens below 1536px so the nav still fits beside the exchange
          rate pill at browser zoom levels above 100%, where the effective
          viewport is narrower than the 1920px the design assumes. */}
      <div className="w-fit flex gap-4 md:gap-5 xl:gap-6 2xl:gap-8 3xl:gap-[50px] py-3 md:py-5 pr-4 md:pr-10 items-center">
        {buttons.map((buttonItem) => (
          <span key={buttonItem} className="hidden lg:block">
            <TertiaryButton buttonName={translate(buttonItem)} id={buttonItem} />
          </span>
        ))}
        <div className="flex text-p_primary subTitle items-center">
          <LanguageChanger image="chevron-down-blue" />
        </div>
        <div
          data-testid="profile"
          className="h-[40px] w-[40px] md:h-[60px] md:w-[60px] bg-p_light rounded-full flex items-center justify-center cursor-pointer shrink-0"
          onClick={() => navigate('/view-profile')}
        >
          <p className="w-full text-center text-sm md:text-base">{initials}</p>
        </div>
        <div className="relative shrink-0" ref={notificationRef}>
          <div className="relative">
            <Image
              src="notifications"
              className="h-5 w-5 md:h-6 md:w-6 cursor-pointer"
              onClick={() => setIsNotificationOpen(!isNotificationOpen)}
              testId={'notifications'}
            />
            {hasUnreadNotifications && (
              <div className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full" />
            )}
          </div>
          <NotificationDropdown
            isOpen={isNotificationOpen}
            onClose={() => setIsNotificationOpen(false)}
            bellIconRef={notificationRef}
            onUnreadStatusChange={setHasUnreadNotifications}
          />
        </div>
        <Image src="log-out" className="h-5 w-5 md:h-6 md:w-6 cursor-pointer shrink-0" onClick={() => setIsOpen(true)} testId={'logout'} />
        <CustomModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title="Logout"
          subText="Are you sure you want to logout?"
          primaryButtonText="Logout"
          secondaryButtonText="Cancel"
          handelSubmit={clickLogout}
          buttonWidth="w-[85px]"
        />
      </div>
    </div>
  );
};

export default Topbar;